import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Logo } from './Logo'
import { Chevron } from './Page'
import { api } from '../lib/api'
import { useSession } from '../lib/session'
import { currentTheme, toggleTheme, type Theme } from '../lib/theme'

interface NavItem {
  to?: string
  label: string
  permission?: string
  /** Leads out of the console rather than within it. */
  back?: boolean
  /** Specified in the console's plan but not built. Shown, never omitted. */
  planned?: boolean
}

/**
 * The console's rail, in the order board 08 sets it.
 *
 * Four of these screens are specified and not built. They stay in the rail,
 * dimmed and inert, for the same reason the stage plan lists a stage that does
 * not exist: an operator is entitled to see the whole plan and where it
 * stopped. Omitting them would make the console look finished.
 */
const NAV: NavItem[] = [
  { to: '/search', label: 'Reader', back: true },
  { to: '/admin', label: 'Overview' },
  { label: 'Publications', planned: true },
  { label: 'Editions', planned: true },
  { to: '/admin/ingest', label: 'Ingest', permission: 'admin.ingest.manage' },
  { to: '/review', label: 'Review queue', permission: 'review.queue.view' },
  { to: '/admin/users', label: 'Users', permission: 'institution.users.manage' },
  { to: '/admin/roles', label: 'Roles', permission: 'admin.roles.manage' },
  { to: '/admin/licences', label: 'Licences', permission: 'admin.licences.manage' },
  { to: '/admin/seats', label: 'Sessions', permission: 'admin.sessions.view' },
  { to: '/admin/usage', label: 'Usage', permission: 'admin.usage.view' },
  { to: '/admin/features', label: 'Features', permission: 'admin.feature_flags.manage' },
  { to: '/admin/search-tuning', label: 'Search tuning', permission: 'admin.search_tuning' },
  { to: '/admin/audit', label: 'Audit', permission: 'admin.audit.view' },
  { label: 'Workers', planned: true },
  { label: 'Storage', planned: true },
]

interface Health {
  status: string
  checks: Record<string, string>
  version: string
}

/**
 * The operator's console.
 *
 * Deliberately a different temperament from the reader: dense,
 * information-first, and unglamorous. An administrator is here to find out
 * what a thing is doing, so nothing rounds a number, nothing hides a
 * traceback, and a stage that does not exist yet says so rather than being
 * omitted.
 *
 * The left rail persists here where the reader gets a bar, because these
 * screens are lists and forms rather than a broadsheet, and width is not the
 * scarce dimension.
 */
export function AdminLayout() {
  const { me, config, can, signOut } = useSession()
  const [theme, setTheme] = useState<Theme>(currentTheme())

  // The version line is measured, not asserted: it is what the running
  // process reports about itself.
  const health = useQuery({
    queryKey: ['healthz'],
    queryFn: () => api.get<Health>('/healthz'),
    staleTime: 60_000,
  })

  const visible = NAV.filter(
    (item) => !item.permission || can(item.permission) || can('admin.users.manage'),
  )

  // On a phone the strip scrolls sideways, so the current section is
  // brought into view rather than left off the edge.
  const rail = useRef<HTMLElement | null>(null)
  const location = useLocation()
  useEffect(() => {
    rail.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [location.pathname])

  return (
    <div className="flex min-h-full flex-col bg-paper md:flex-row">
      {/* On a phone the rail is a strip pinned to the foot of the screen, as
          the prototype's tab bar is. It used to sit after the content, below
          the last row of whatever list was open, where nobody found it. */}
      <nav
        ref={rail}
        aria-label="Sections"
        className="sticky bottom-0 z-20 order-2 flex shrink-0 gap-1 overflow-x-auto border-t border-rule bg-surface p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]
          md:static md:order-1 md:w-[196px] md:flex-col md:gap-px md:overflow-visible md:border-r md:border-t-0 md:p-0"
      >
        <div className="hidden border-b border-n300 px-3.5 pb-3 pt-3.5 md:block">
          <div className="flex items-center gap-2">
            <Logo size={16} />
            <span className="whitespace-nowrap text-[14.5px] font-semibold">
              {config?.product_name ?? ''}
            </span>
          </div>
          <div className="pl-[23px] font-mono text-[10px] uppercase tracking-[0.08em] text-ink2">
            Admin console
          </div>
        </div>

        <div className="flex gap-1 md:flex-1 md:flex-col md:gap-px md:p-2">
          {visible.map((item) =>
            item.planned || !item.to ? (
              <div
                key={item.label}
                aria-disabled="true"
                title="Specified in the console's plan and not built yet."
                className="whitespace-nowrap rounded-[--radius-md] px-2.5 py-1.5 text-[13.5px] text-ink4"
              >
                {item.label}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/admin' || item.to === '/'}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 whitespace-nowrap rounded-[--radius-md] px-2.5 py-1.5 text-[13.5px] no-underline ${
                    isActive
                      ? 'bg-paper font-semibold text-body shadow-[inset_0_0_0_1px_var(--jl-accent)]'
                      : 'text-ink2 hover:text-body'
                  }`
                }
              >
                {item.back && <Chevron direction="left" size={11} />}
                {item.label}
              </NavLink>
            ),
          )}
          <a
            href="/docs/"
            className="whitespace-nowrap rounded-[--radius-md] px-2.5 py-1.5 text-[13.5px] text-accent no-underline"
          >
            Documentation
          </a>
        </div>

        <div className="mt-auto hidden border-t border-n300 px-3.5 py-[11px] md:block">
          <div className="font-mono text-[9.5px] uppercase leading-[1.75] tracking-[0.02em] text-ink2">
            <div className="text-ink">
              v{health.data?.version ?? '0.0.0'} · {config?.environment ?? 'unknown'}
            </div>
            {Object.entries(health.data?.checks ?? {}).map(([name, state]) => (
              <div key={name}>
                {name.replace(/_/g, ' ')} {state}
              </div>
            ))}
            <div>{me?.institution?.name ?? 'no institution'}</div>
          </div>
        </div>
      </nav>

      <div className="order-1 flex min-w-0 flex-1 flex-col md:order-2">
        <header className="flex items-center justify-between gap-4 border-b border-rule bg-surface px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold md:hidden">
              {config?.product_name ?? ''}
            </div>
            <div className="ident truncate">
              {me?.email} · {me?.institution?.name ?? 'no institution'} ·{' '}
              {me?.roles.join(', ') || 'no roles'}
            </div>
          </div>
          <div className="flex flex-none items-center gap-2">
            <button
              type="button"
              className="btn-secondary whitespace-nowrap"
              onClick={() => setTheme(toggleTheme())}
              aria-label={`Switch to the ${theme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {theme === 'dark' ? 'Light' : 'Dark'}
            </button>
            <button
              type="button"
              className="btn-secondary whitespace-nowrap"
              onClick={() => void signOut()}
            >
              Sign out
            </button>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
